import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Papa from "papaparse";

import { EmptyContainer } from "src/components/emptyContainer";
import { PageLoader } from "src/components/pageLoader";
import { StringColumn } from "src/components/tables/columnConfigs/stringColumn";
import TypedTable from "src/components/typedTable";
import { VFlex } from "src/components/vFlex";
import { ResultFormat } from "src/gen/schema/bilge/api";

import { MockShellItemHeader } from "./mockShellItemHeader";
import { CommandHistory, CommandOutput } from "./mockShellSessionManager";
import { copyMockshellCmds } from "./mockShellUtils";

import "./mockShellHistoryList.scss";

type CsvData = Record<string, string>;

interface MockShellHistoryListProps {
  readonly history: CommandHistory;
  readonly loading: boolean;
}

interface HistoryItemProps {
  readonly item: CommandOutput;
}

const HistoryEmptyData = ({ item }: HistoryItemProps) => {
  return (
    <VFlex
      className="mock-shell-history-item"
      data-qa="mock-shell-history-item-empty"
    >
      <MockShellItemHeader
        command={item.command}
        executedAt={item.executedAt}
        showCopyButton={false}
        showPopOutButton={false}
        selectedDeviceId={item.deviceId}
      />
      <div>
        <EmptyContainer text={item.emptyStateText ?? "No data found"} />
      </div>
    </VFlex>
  );
};

// CSV Table component for history items
const HistoryItemCsv = ({ item }: HistoryItemProps) => {
  const [tableData, setTableData] = useState<CsvData[]>([]);
  const [tableColumn, setTableColumn] =
    useState<Record<string, StringColumn>>();
  const divRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (item.output) {
      Papa.parse<CsvData>(item.output, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          if (result.data && result.data.length > 0) {
            const columns = Object.keys(result.data[0] as CsvData).reduce(
              (acc: Record<string, StringColumn>, key) => {
                acc[key] = new StringColumn({
                  label: [key]
                });
                return acc;
              },
              {}
            );
            setTableColumn(columns);
            setTableData(result.data);
          }
        }
      });
    }
  }, [item.output]);

  const handleCopy = useCallback(() => {
    copyMockshellCmds(divRef);
  }, []);

  if (tableData.length === 0) {
    return <HistoryEmptyData item={item} />;
  }

  return (
    <VFlex
      className="mock-shell-history-item"
      data-qa="mock-shell-history-item-csv"
      ref={divRef}
    >
      <MockShellItemHeader
        command={item.command}
        executedAt={item.executedAt}
        onCopy={handleCopy}
        selectedDeviceId={item.deviceId}
      />
      <div className="mock-shell-history-item-table">
        {tableColumn && (
          <TypedTable
            stickyHeader={false}
            name="mock-shell-history-table"
            columns={tableColumn}
            data={tableData}
          />
        )}
      </div>
    </VFlex>
  );
};

// Text output component for history items
const HistoryItemText = ({ item }: HistoryItemProps) => {
  const divRef = useRef<HTMLDivElement>(null);

  const handleCopy = useCallback(() => {
    copyMockshellCmds(divRef);
  }, []);

  if (item.output === "") {
    return <HistoryEmptyData item={item} />;
  }

  return (
    <VFlex
      className="mock-shell-history-item"
      data-qa="mock-shell-history-item-text"
      ref={divRef}
    >
      <MockShellItemHeader
        command={item.command}
        executedAt={item.executedAt}
        onCopy={handleCopy}
        selectedDeviceId={item.deviceId}
      />
      <pre className="mock-shell-history-item-output">{item.output}</pre>
    </VFlex>
  );
};

// Main history item component that renders CSV or Text based on format
const HistoryItem = ({ item }: HistoryItemProps) => {
  if (item.resultFormat === ResultFormat.CSV) {
    return <HistoryItemCsv item={item} />;
  }
  return <HistoryItemText item={item} />;
};

export const MockShellHistoryList = ({
  history,
  loading
}: MockShellHistoryListProps) => {
  // Display in reverse order: oldest at top, newest at bottom
  const reversedCommands = useMemo(
    () => [...history.commands].reverse(),
    [history.commands]
  );
  if (history.commands.length === 0) {
    return null;
  }

  return (
    <VFlex
      className="mock-shell-history-list"
      gap="sm"
      data-qa="mock-shell-history"
    >
      {reversedCommands.map((item) => (
        <HistoryItem key={item.id} item={item} />
      ))}
      {loading && (
        <VFlex
          align="center"
          justify="center"
          className="mock-shell-output-loader"
        >
          <PageLoader />
        </VFlex>
      )}
    </VFlex>
  );
};
